import React, { useMemo } from "react"
import { AccordionWithChildren } from "swiipe.portal.shared"
import useReduxDispatch from "../../../store/useReduxDispatch"
import { ITranslationItem } from "../../../type/translations/ITranslationItem"
import TranslationChangesBadge from "./TranslationChangesBadge"
import TranslationItem from "./TranslationItem"
import TranslationMissingBadge from "./TranslationMissingBadge"
import "./TranslationSection.scss"
import TranslationSelection from "./TranslationSelection"
import TranslationTagField from "./TranslationTagField"

interface ITranslationSectionProps {
    keyLevels?: string[]
    groupItems: ITranslationItem[]
    label?: string
    subLabel?: string
}

interface ISection {
    type: "section"
    key: string
    keyLevels: string[]
    items: ITranslationItem[]
}

interface IItem {
    type: "item"
    item: ITranslationItem
}

type IToRender = ISection | IItem

const TranslationSection = ({ keyLevels, groupItems, label, subLabel }: ITranslationSectionProps) => {
    const dispatch = useReduxDispatch()
    const levels = keyLevels ?? []
    const key = levels.join(".")

    const relevantItems = useMemo(
        () => groupItems.filter((gi) => levels.every((kl, index) => kl === gi.levels[index])),
        [groupItems, key]
    )
    const toRenders = relevantItems.reduce((res, ri) => {
        if (ri.levels.length === levels.length + 1) {
            res.push({ type: "item", item: ri })
            return res
        }
        const nextLevel = ri.levels[levels.length]
        const nextLevelKey = `${key}.${nextLevel}`
        const section = res.find((r) => r.type === "section" && r.key === nextLevelKey) as ISection | undefined
        if (section) {
            section.items.push(ri)
        } else {
            res.push({
                type: "section",
                key: nextLevelKey,
                keyLevels: [...levels, nextLevel],
                items: [ri],
            })
        }
        return res
    }, [] as IToRender[])

    return (
        <div className="translation-section">
            <AccordionWithChildren
                classNameHeader="translation-section__accordion-header"
                header={
                    <>
                        <TranslationSelection
                            className="translation-section__selection"
                            group={relevantItems[0]?.group ?? ""}
                            levels={keyLevels}
                        />
                        <b>
                            {label ?? key}
                            {subLabel && <span className="translation-section__sub-label">{subLabel}</span>}
                        </b>
                        <TranslationChangesBadge items={relevantItems} className="translation-section__changes-badge" />
                        <TranslationMissingBadge items={relevantItems} className="translation-section__missing-badge" />
                        <TranslationTagField
                            group={relevantItems[0]?.group ?? ""}
                            subKey={key}
                            className="translation-section__tags"
                        />
                    </>
                }
                animate={false}
                leftAlignHeader
                arrowLeftOfHeader
                onlyRenderVisibleChildren
                ignoreHeaderClick
            >
                <div className="translation-section__content">
                    {toRenders.map((toRender) => {
                        if (toRender.type === "section") {
                            return (
                                <TranslationSection
                                    key={toRender.key}
                                    groupItems={relevantItems}
                                    keyLevels={toRender.keyLevels}
                                />
                            )
                        } else {
                            return <TranslationItem key={toRender.item.key} item={toRender.item} />
                        }
                    })}
                </div>
            </AccordionWithChildren>
        </div>
    )
}

export default React.memo(TranslationSection)
